/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useEffect, useState, useMemo} from 'react';
import styled from 'styled-components';

import MuiForm from 'components/MuiForm';
import {ActionButton} from 'screens/commons';
import {getSchema} from './PersonalDetailsSchema';

const BASE_REQUIRED_FIELDS = [
  'fathers_name',
  'gender',
  'marital_status',
  'occupation',
  'gross_annual_income',
];

const PER_ADDRESS_REQUIRED_FIELDS = [
  'per_add_line1',
  'per_add_pincode',
  'per_add_district_or_city',
  'per_add_country',
];

const CORR_ADDRESS_REQUIRED_FIELDS = [
  'corr_add_line1',
  'corr_add_pincode',
  'corr_add_district_or_city',
  'corr_add_country',
];

const PersonalDetails = ({formSchema, addressOptions, handleFormSubmit}) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const stepperContainer = document.querySelector('[class*="meta-form-stepper"]');
    if (!!stepperContainer) {
      stepperContainer.classList.add('stepper');
    }
  }, []);

  const requiredFields = useMemo(() => {
    const fields = [...BASE_REQUIRED_FIELDS];

    const isCopyFromPermanent =
      formData.copy_from_permanent === 'true' || formData.copy_from_permanent === true;

    if (formData.perAddressSelector === 'new_address') {
      fields.push(...PER_ADDRESS_REQUIRED_FIELDS);
    }

    if (formData.corrAddressSelector === 'new_address' && !isCopyFromPermanent) {
      fields.push(...CORR_ADDRESS_REQUIRED_FIELDS);
    }

    return fields;
  }, [formData.perAddressSelector, formData.corrAddressSelector, formData.copy_from_permanent]);

  const isComplete = requiredFields.every((key) => {
    const value = formData?.[key];
    return value !== '' && value !== null && value !== undefined;
  });

  const schema = useMemo(
    () => getSchema(structuredClone(formSchema), addressOptions),
    [formSchema, addressOptions]
  );

  return (
    <Wrapper>
      <CardContent>
        <MuiForm
          className="personal-details-form"
          schema={schema}
          onChange={(_, data) => setFormData(data || {})}
          onSubmit={(data) => handleFormSubmit(data)}
          buttons={{
            submit: <ActionButton buttonText={'Continue'} isDisabled={!isComplete} />,
          }}
        />
      </CardContent>
    </Wrapper>
  );
};

// Styled Components
const Wrapper = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CardContent = styled.div`
  padding: 0.5rem;
  flex: 1;
  overflow-y: auto;

  .personal-details-form .input-container .Mui-disabled::after {
    content: none !important;
    display: none !important;
  }

  .input-container div {
    overflow: hidden !important;
  }

  .mcol {
    flex: none !important;
    width: fit-content !important;
    margin-top: 0.5rem !important;
    padding-left: 0 !important;
  }
  .MuiButton-root {
    font-size: 0.9rem !important;
  }

  .Mui-disabled input {
    padding-left: 0.9rem !important;
    background-color: #d3d3d32a !important;
  }

  .personal-details-form form > div > :last-child {
    margin-bottom: 0.8rem !important;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.background.secondary || '#f1f5f9'};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.brand.primary || '#7c3aed'};
    border-radius: 8px;
  }

  @media (min-width: 768px) {
    padding: 1.5rem 3rem;
  }
`;

export default PersonalDetails;
